import { VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import {
  useStoreCompanyClient,
  useUpdateCompanyClient,
  searchCompaniesFetcher,
} from "hooks/companies";
import { useGetCurrencyByUserAndCompany } from "hooks/shares";
import { useGetDepositories } from "hooks/shares";
import { useGetCountriesCurrency } from "hooks/countries";
import { useGetIndustriesHook } from "hooks/industries";
import { useRef, useState } from "react";
import moment from "moment";
import {
  Button,
  CalendarInput,
  Checkbox,
  DropFileInput,
  FullscreenModal,
  LargeSwitch,
  SelectComponent,
  AsyncSelectComponent,
  Input,
  Textarea,
  Warning,
} from "shared";
import { CompanyMyType, CompanyStoreClientType } from "types/CompanyTypes";
import { getCurrencyFromUuid, scrollToError } from "utils";
import * as Yup from "yup";
import { isValidPolishNip } from "utils/validation/nipValidation";

interface AddCompanyClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  company?: CompanyMyType | null;
  defaultType?: "stock" | "share";
}

const AddCompanyClientModal: React.FC<AddCompanyClientModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  company,
  defaultType,
}) => {
  const { mutateAsync: storeCompany } = useStoreCompanyClient();
  const { mutateAsync: updateCompany } = useUpdateCompanyClient();
  const { data: industries } = useGetIndustriesHook();
  const { data: currencyOptions } = useGetCountriesCurrency();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<any>(null);
  const [isIndustryDisabled, setIsIndustryDisabled] = useState(false);
  const [isTabDisabled, setIsTabDisabled] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [isArchivedCompany, setIsArchivedCompany] = useState<boolean>(false);
  const [isInvalidPricePerShare, setIsInvalidPricePerShare] =
    useState<boolean>(false);

  const { data: userCurrencies } = useGetCurrencyByUserAndCompany(
    selectedCompanyId,
    {
      enabled: !!selectedCompanyId,
    },
  );
  const { data: depositoryOptions } = useGetDepositories(isOpen);

  const handleOnClose = () => {
    onClose();
    setIsIndustryDisabled(false);
    setIsTabDisabled(false);
  };

  const initialValues = {
    name: company?.name || "",
    industryUuid: company?.industryUuid || "",
    currencyUuid: company?.currencyUuid || "",
    sharesCount: company?.sharesCount || null,
    sharesValue: company?.sharesValue || null,
    sharesId: company?.shareId || null,
    companyId: company?.id || null,
    publicationDate: company?.publicationDate || null,
    invoice: null,
    completedAt:
      company?.firstTransaction?.completedAt || moment().format("YYYY-MM-DD"),
    type: company?.type || defaultType || "stock",
    pricePerShare: company?.pricePerShare || null,
    nip: company?.nip || "",
    isHistoryAccepted: company?.isHistoryAccepted || false,
    companyUuid: null,
    additionalInfo: company?.description || "",
    depositoryType: (company as any)?.depositoryType ?? null,
    depositoryId: (company as any)?.depositoryId ?? null,
  };
  const loadCompanyOptions = async (inputValue: string) => {
    const q = inputValue.trim();
    if (q.length < 2) return [];
    const items = await searchCompaniesFetcher(
      q,
      undefined,
      undefined,
      defaultType,
    );
    return items.map((c) => ({ label: c.name, value: c.name, company: c }));
  };

  const truncateTo2 = (n: number) => Math.floor(n * 100) / 100;

  const onSubmit = async (values: CompanyStoreClientType) => {
    setIsSubmitting(true);
    try {
      const raw =
        !company && values.sharesValue && values.sharesCount
          ? values.sharesValue / values.sharesCount
          : 0;

      const summary =
        Number.isFinite(raw) && !Number.isNaN(raw) ? truncateTo2(raw) : 0;

      if (!!company) {
        const {
          sharesCount: _ignoreSharesCount,
          sharesValue: _ignoreSharesValue,
          invoice: _ignoreInvoice,
          currencyUuid: _ignoreCurrencyUuid,
          ...restValues
        } = values as any;

        await updateCompany(
          {
            ...company,
            ...restValues,
            type: company.type === "stock" ? "stock" : restValues.type,
            sharesId: company.sharesId,
            pricePerShare: company.pricePerShare,
            nip: restValues.nip?.trim() || null,
          },
          {
            onSuccess: () => {
              handleOnClose();
            },
          },
        );
      } else {
        await storeCompany(
          {
            ...values,
            pricePerShare: summary,
            nip: values.nip?.trim() || null,
          },
          {
            onSuccess: (data) => {
              onSuccess && onSuccess(data);
              handleOnClose();
            },
          },
        );
      }
    } finally {
      scrollToError(formRef.current?.errors);
      setIsSubmitting(false);
    }
  };

  const validation = () =>
    Yup.object().shape({
      name: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      industryUuid: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      currencyUuid: Yup.string().required(VALIDATION_ENUM.REQUIRED),

      sharesCount: Yup.number()
        .min(1, VALIDATION_ENUM.MIN_1)
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return null;
          const casted =
            typeof originalValue === "string" ? Number(originalValue) : value;
          return Number.isNaN(casted) ? null : casted;
        })
        .when(["type"], {
          is: (type: string) => type === "stock",
          then: (schema) =>
            company
              ? schema
              : schema
                  .required(VALIDATION_ENUM.REQUIRED)
                  .min(1, VALIDATION_ENUM.MIN_1)
                  .test(
                    "stock-integer",
                    "Ilość akcji musi być liczbą całkowitą",
                    (value) => value == null || Number.isInteger(value),
                  ),
          otherwise: (schema) =>
            company ? schema : schema.required(VALIDATION_ENUM.REQUIRED),
        }),

      completedAt: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      sharesValue: Yup.number()
        .nullable()
        .optional()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return null;
          const casted =
            typeof originalValue === "string" ? Number(originalValue) : value;
          return Number.isNaN(casted) ? null : casted;
        }),

      type: Yup.string().required(VALIDATION_ENUM.REQUIRED),
      isHistoryAccepted: Yup.boolean(),
      depositoryId: Yup.number()
        .nullable()
        .when("depositoryType", {
          is: (v: string | null) => !!v,
          then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
          otherwise: (schema) => schema.notRequired(),
        }),
      nip: Yup.string()
        .max(11)
        .matches(/^\d*$/, "NIP może zawierać tylko cyfry")
        .test("nip-control", "Niepoprawny numer NIP", (value) => {
          const v = value?.trim();
          if (!v) return true;
          return isValidPolishNip(v);
        }),
    });

  return (
    <FullscreenModal isOpen={isOpen} onClose={handleOnClose}>
      <h2 className="fullscreen-modal__header">
        {!!company
          ? "Edycja spółki zakupionej poza Reliance"
          : "Dodaj spółkę zakupioną poza Reliance"}
      </h2>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values: any) => await onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
        innerRef={formRef}
        validateOnMount={false}
      >
        {({ errors, values, setFieldValue, submitForm }) => {
          const summaryRaw =
            !company &&
            !!values?.sharesValue &&
            !!values?.sharesCount &&
            values?.sharesValue > 0 &&
            values?.sharesCount > 0
              ? values?.sharesValue / values?.sharesCount
              : 0;

          const summary =
            Number.isFinite(summaryRaw) && !Number.isNaN(summaryRaw)
              ? truncateTo2(summaryRaw)
              : 0;

          const shouldValidatePrice =
            !company &&
            values?.sharesValue !== null &&
            values?.sharesValue !== undefined &&
            values?.sharesCount !== null &&
            values?.sharesCount !== undefined;

          if (shouldValidatePrice) {
            setTimeout(() => {
              setIsInvalidPricePerShare(summary <= 0);
            }, 0);
          } else if (isInvalidPricePerShare) {
            setTimeout(() => {
              setIsInvalidPricePerShare(false);
            }, 0);
          }

          const isEditingStockCompany = !!company && company.type === "stock";
          const effectiveType = isEditingStockCompany ? "stock" : values?.type;
          const isStock = effectiveType === "stock";
          const hasExistingCurrencyPool =
            Array.isArray(userCurrencies) &&
            !!values.currencyUuid &&
            userCurrencies.includes(values.currencyUuid);
          const hasSelectedCompany = !!values.companyUuid;

          return (
            <Form className="m-width-100 fullscreen-modal__select-margin">
              {!company && !isEditingStockCompany && !defaultType && (
                <LargeSwitch
                  disabled={isTabDisabled}
                  state={values.type}
                  className="add-company__switch"
                  options={[
                    { label: "Akcje", value: "stock" },
                    { label: "Udziały", value: "share" },
                  ]}
                  onChange={(value: string) => setFieldValue("type", value)}
                />
              )}
              {!!company ? (
                <Field
                  type="text"
                  id="name"
                  name="name"
                  label={"Nazwa spółki pacanie"}
                  as={Input}
                  maxLength={50}
                />
              ) : (
                <Field
                  id="name"
                  name="name"
                  className="select--no-arrow"
                  maxLength={50}
                  label={"Nazwa spółki"}
                  as={AsyncSelectComponent as any}
                  loadOptions={loadCompanyOptions}
                  minCharsToSearch={3}
                  onSelectOption={(opt: any) => {
                    const c = opt?.company as any;
                    if (c && c.type) {
                      setFieldValue(
                        "type",
                        c.type === "stock" ? "stock" : "share",
                      );
                      setIsTabDisabled(true);
                    }
                    if (c && c.industryUuid) {
                      setFieldValue("industryUuid", c.industryUuid);
                      setIsIndustryDisabled(true);
                      setFieldValue("companyUuid", c.uuid || null);
                    } else {
                      setIsIndustryDisabled(false);
                      setIsTabDisabled(false);
                      setFieldValue("companyUuid", null);
                    }
                    setSelectedCompanyId(
                      typeof c?.id === "number"
                        ? c.id
                        : typeof c?.companyId === "number"
                        ? c.companyId
                        : null,
                    );
                    setIsArchivedCompany(!!c?.isArchived);
                  }}
                  onInputTyping={() => {
                    setIsIndustryDisabled(false);
                    setIsTabDisabled(false);
                    setFieldValue("companyUuid", null);
                    setSelectedCompanyId(null);
                    setIsArchivedCompany(false);
                  }}
                />
              )}

              <Field
                label="Branża"
                name="industryUuid"
                id="industryUuid"
                as={SelectComponent}
                options={industries}
                disabled={isIndustryDisabled}
              />

              {!hasSelectedCompany && (
                <Field
                  type="date"
                  id="publicationDate"
                  name="publicationDate"
                  label={"Data publikacji na giełdzie"}
                  optional
                  component={CalendarInput}
                />
              )}

              {!company && (
                <Field
                  label="Waluta"
                  name="currencyUuid"
                  id="currencyUuid"
                  as={SelectComponent}
                  options={currencyOptions}
                />
              )}

              {hasExistingCurrencyPool && !company && (
                <div className="input__hint" aria-live="polite">
                  {isStock
                    ? `${
                        isArchivedCompany ? "Miałeś już" : "Masz już"
                      } akcje spółki w tej walucie. Kontynuując, dodasz nową transakcję do swojej puli.`
                    : `${
                        isArchivedCompany ? "Miałeś już" : "Masz już"
                      } udziały w tej spółce, w tej walucie. Kontynuując, dodasz nową transakcję do swojej puli.`}
                </div>
              )}

              {!company && (
                <Field
                  type="number"
                  id="sharesCount"
                  name="sharesCount"
                  label={`Ilość  ${isStock ? "akcji" : "udziałów"}`}
                  as={Input}
                  maxLength={10}
                  precision={isStock ? 0 : 2}
                  inputMode={isStock ? "numeric" : "decimal"}
                  step={isStock ? 1 : "any"}
                />
              )}

              {!company && (
                <div className="input--currency">
                  <Field
                    type="number"
                    id="sharesValue"
                    name="sharesValue"
                    precision={2}
                    label={`Wartość  ${isStock ? "akcji" : "udziałów"}`}
                    as={Input}
                    maxLength={10}
                    optional
                  />
                  <span>{getCurrencyFromUuid(values.currencyUuid)}</span>
                </div>
              )}

              {!company && isInvalidPricePerShare && (
                <Warning
                  text={`Wartość ${
                    isStock ? "jednej akcji" : "jednego udziału"
                  } musi być większa niż 0.`}
                />
              )}

              {!company && (
                <div className={`add-share__summary`}>
                  <label className="input__label">
                    Wartość {isStock ? "jednej akcji" : "jednego udziału"}
                  </label>
                  <input
                    type="text"
                    className="input"
                    readOnly
                    value={(() => {
                      const n = Number(summary || 0);
                      return n % 1 === 0 ? String(n) : n.toFixed(2);
                    })()}
                  />
                  <span>
                    {values.currencyUuid &&
                      getCurrencyFromUuid(values.currencyUuid)}
                  </span>
                </div>
              )}
              {!company && (
                <Field
                  type="date"
                  id="completedAt"
                  name="completedAt"
                  label={
                    hasExistingCurrencyPool
                      ? "Data transakcji"
                      : `Data pierwszego zakupu ${
                          effectiveType === "stock" ? "akcji" : "udziałów"
                        }`
                  }
                  component={CalendarInput}
                  errors={errors}
                />
              )}

              {isStock && (
                <>
                  <Field
                    label="Miejsce deponowania"
                    name="depositoryType"
                    id="depositoryType"
                    optional
                    as={SelectComponent}
                    isClearable
                    value={
                      [
                        { label: "Konto Maklerskie", value: "brokerage" },
                        { label: "Rejestr Akcjonariuszy", value: "registry" },
                      ].find((o) => o.value === values.depositoryType) ?? null
                    }
                    options={[
                      { label: "Konto Maklerskie", value: "brokerage" },
                      { label: "Rejestr Akcjonariuszy", value: "registry" },
                    ]}
                    onChange={(option: { value: string } | null) => {
                      setFieldValue(
                        "depositoryType",
                        option ? option.value : null,
                      );
                      setFieldValue("depositoryId", null);
                    }}
                  />

                  {values.depositoryType && (
                    <Field
                      label={
                        values.depositoryType === "brokerage"
                          ? "Konto maklerskie"
                          : "Rejestr akcjonariuszy"
                      }
                      name="depositoryId"
                      id="depositoryId"
                      as={SelectComponent}
                      isClearable
                      value={
                        depositoryOptions?.find(
                          (o) => o.value === values.depositoryId,
                        ) ?? null
                      }
                      options={depositoryOptions ?? []}
                      onChange={(option: { value: number } | null) => {
                        setFieldValue(
                          "depositoryId",
                          option ? option.value : null,
                        );
                      }}
                    />
                  )}
                </>
              )}
              {!hasSelectedCompany && (
                <Field
                  component={Textarea}
                  id="additionalInfo"
                  name="additionalInfo"
                  label={"Dodatkowe informacje"}
                  optional
                  as={Textarea}
                />
              )}

              {!company && (
                <Field
                  label={<>Umowa</>}
                  name="invoice"
                  id="invoice"
                  as={DropFileInput}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  maxSize={5}
                  description="Maks. 5 MB (PDF, DOC, DOCX, JPG, JPEG, PNG)"
                  optional
                />
              )}
              {!hasExistingCurrencyPool && !hasSelectedCompany && (
                <Field
                  type="number"
                  id="nip"
                  name="nip"
                  optional
                  label={"NIP"}
                  as={Input}
                  maxLength={11}
                />
              )}

              {!hasExistingCurrencyPool && (
                <Field
                  id="isHistoryAccepted"
                  name="isHistoryAccepted"
                  wrapperClassName="login-wrapper__acceptance checkbox__modal"
                  label={
                    <p>
                      Udzielam zgody, by pracownicy firmy Reliance mieli dostęp
                      do historii moich transakcji związanych z tą spółką.
                    </p>
                  }
                  as={Checkbox}
                />
              )}

              <div className="fullscreen-modal__buttons">
                <Button
                  label="Anuluj"
                  onClick={handleOnClose}
                  className="button--stroke button--rounded"
                />
                <Button
                  label={"Potwierdź"}
                  type="button"
                  onClick={async () => {
                    await submitForm().then(() => {
                      if (
                        Object.keys(formRef.current?.errors || {}).length > 0
                      ) {
                        scrollToError(formRef.current?.errors);
                      }
                    });
                  }}
                  className=" button--rounded button--black"
                  isLoading={isSubmitting}
                  disabled={isInvalidPricePerShare}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </FullscreenModal>
  );
};

export default AddCompanyClientModal;
